import React, { useState, useEffect } from 'react';
import { 
  isTestingModeActive, 
  disableTestingMode, 
  resetTestingDatabase, 
  seedSampleTestingData, 
  getTestingStorageStats 
} from '@/utils/devTestingSandbox';
import { Button } from '@/components/ui/button';
import { 
  FlaskConical, 
  RotateCcw, 
  LogOut, 
  Sparkles, 
  Database, 
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  HardDrive
} from 'lucide-react';

export default function TestingModeBanner() {
  const [active, setActive] = useState(false);
  const [stats, setStats] = useState({ totalItems: 0, entities: {} });
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    const isAct = isTestingModeActive();
    setActive(isAct);
    if (isAct) {
      setStats(getTestingStorageStats());
    }
  }, []);

  if (!active) return null;

  return (
    <>
      {/* Floating Sandbox Bar */}
      <aside aria-label="Developer Testing Sandbox Bar" className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 z-50 transition-all duration-300">
        <div className="bg-[#1E1B18]/95 backdrop-blur-md border-2 border-amber-500/80 text-white rounded-2xl shadow-2xl p-3 md:p-4 max-w-xl w-full">
          
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-amber-500 text-black flex items-center justify-center font-bold shrink-0 animate-pulse">
                <FlaskConical className="w-5 h-5 text-[#1E1B18]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black tracking-wider uppercase text-amber-400">Mode Testing Developer</span>
                  <span className="bg-amber-400/20 text-amber-300 text-[10px] px-2 py-0.5 rounded-full font-bold border border-amber-400/30">
                    Lokal Browser
                  </span>
                </div>
                {!minimized && (
                  <p className="text-[11px] text-zinc-300 mt-0.5">
                    Tersimpan {stats.totalItems} item lokal di perangkat ini. Database live aman.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button 
                onClick={() => setMinimized(!minimized)}
                className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
                title={minimized ? "Buka panel" : "Perkecil panel"}
              >
                {minimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {!minimized && (
            <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setShowConfirmReset(true)}
                  className="h-8 text-xs bg-transparent border-red-400/60 text-red-300 hover:bg-red-950/50 hover:text-red-200"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Kosongkan DB
                </Button>

                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={seedSampleTestingData}
                  className="h-8 text-xs bg-transparent border-amber-400/60 text-amber-300 hover:bg-amber-950/50 hover:text-amber-200"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> Muat Data Demo
                </Button>
              </div>

              <Button 
                size="sm" 
                onClick={disableTestingMode}
                className="h-8 text-xs bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold shadow-md"
              >
                <LogOut className="w-3.5 h-3.5 mr-1" /> Kembali ke Normal
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* Confirmation Modal to Reset Sandbox Database */}
      {showConfirmReset && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-red-200 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 mx-auto flex items-center justify-center mb-3">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-base font-black text-[#302621]">Kosongkan Database Testing?</h3>
            <p className="text-xs text-[#7A695B] mt-1.5 leading-relaxed">
              Semua item dummy yang Anda tambahkan di mode testing akan dihapus bersih dari perangkat ini.
            </p>
            <div className="flex gap-2 mt-5">
              <Button 
                variant="outline" 
                onClick={() => setShowConfirmReset(false)}
                className="flex-1 text-xs"
              >
                Batal
              </Button>
              <Button 
                variant="destructive" 
                onClick={resetTestingDatabase}
                className="flex-1 text-xs font-bold"
              >
                Ya, Kosongkan
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
